import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  CustomTableFilterBar,
  DateFilterTemplate,
  ETypes,
  RColumn,
  RemoteTable,
  multiSelectFilterTemplate,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { EMPTY_VALUE, formatIsoMoment, textOrDash } from '../../../components/summary/summaryFormat'
import {
  HospitalizationEvolutionRowDTO,
  HospitalizationFilterDTO,
  HospitalizationOptionDTO,
} from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { HospitalizationTableParams, getHospitalizationEvolutionRows } from '../../../services/HospitalizationSummaryController'
import useHospitalizationGrid from '../useHospitalizationGrid'
import HospitalizationGridNotice from './HospitalizationGridNotice'

/** Clave con la que RemoteTable guarda filtros y orden en el navegador. */
export const EVOLUTIONS_TABLE_ID = 'HospitalizationEvolutionsTable'

/** Tipo de dato de cada columna con filtro: el texto viaja como «contiene» y la fecha como desde / hasta. */
const FILTERS_TYPE = [
  { field: 'patientName', type: ETypes.string },
  { field: 'professionalName', type: ETypes.string },
  { field: 'scoreId', type: ETypes.integer },
  { field: 'sectorName', type: ETypes.string },
  { field: 'observation', type: ETypes.string },
  { field: 'date', type: ETypes.dateTime },
]

const DEFAULT_SORT = [{ field: 'date', order: -1 as const }]

/** Ancho mínimo de cada columna, en px (los pesos `grow`; su suma es el ancho mínimo de la tabla). */
const COLUMN_WIDTH = {
  patient: 176,
  professional: 176,
  date: 144,
  type: 128,
  sector: 160,
  observation: 288,
} as const

const TABLE_MIN_WIDTH = Object.values(COLUMN_WIDTH).reduce((sum, width) => sum + width, 0)

const NO_OPTIONS: HospitalizationOptionDTO[] = []

/** Texto de cada «Tipo» elegido en el filtro (el chip muestra el nombre, no el id). */
function optionNames(options: HospitalizationOptionDTO[]): (id: unknown) => string {
  const names = new Map(options.map((option) => [option.id, option.name]))
  return (id) => (typeof id === 'number' ? names.get(id) ?? '' : '')
}

interface HospitalizationEvolutionsTableProps {
  /** Los «Tipo» de evolución para el filtro. */
  types: HospitalizationOptionDTO[] | undefined
  /** Filtros aplicados de la pantalla (período y «Más filtros»): las mismas del resto de las pestañas. */
  filters: HospitalizationFilterDTO
  /** Lo último que pidió la tabla: es lo que exporta «Exportar». */
  onLoad: (params: HospitalizationTableParams) => void
}

/**
 * Evoluciones médicas de las internaciones del período, con el mismo período y los mismos filtros de la pantalla. Solo se
 * ofrece al perfil con la acción de evoluciones médicas (el backend la vuelve a controlar en cada pedido: si responde que
 * no hay acceso, se dice qué hacer en lugar de mostrar la tabla vacía). Tabla paginada contra `/evolutions` con la barra de
 * filtros del framework; «Tipo» (Evolución, Interconsulta, Respuesta) es la columna que antes se llamaba «Puntaje».
 */
export default function HospitalizationEvolutionsTable({ filters, types, onLoad }: HospitalizationEvolutionsTableProps) {
  const intl = useIntl()
  const load = useCallback(
    (offSet: number, pageSize: number, params: HospitalizationTableParams) => getHospitalizationEvolutionRows(offSet, pageSize, params),
    [],
  )
  const { tableRef, params, loadItems, state, retry } = useHospitalizationGrid<HospitalizationEvolutionRowDTO>(
    filters,
    load,
    onLoad,
  )

  const typeList = types ?? NO_OPTIONS
  const typeFilter = useMemo(() => multiSelectFilterTemplate({ list: typeList, optionLabel: 'name', optionValue: 'id', pageSize: true }), [typeList])

  // Buscador por paciente y filtros por campo. Los textos de la barra ya vienen en español en el framework
  const filterBar = useMemo<CustomTableFilterBar>(() => ({
    searchField: 'patientName',
    searchLabel: intl.formatMessage({ id: 'HospitalizationSummaryColPatient' }),
    texts: { search: intl.formatMessage({ id: 'HospitalizationSummarySearchPatient' }) },
    fields: [
      { field: 'professionalName', label: intl.formatMessage({ id: 'HospitalizationSummaryColProfessional' }) },
      { field: 'date', label: intl.formatMessage({ id: 'HospitalizationSummaryColDate' }) },
      { field: 'scoreId', label: intl.formatMessage({ id: 'HospitalizationSummaryColEvolutionType' }), format: optionNames(typeList) },
      { field: 'sectorName', label: intl.formatMessage({ id: 'HospitalizationSummaryColSector' }) },
      { field: 'observation', label: intl.formatMessage({ id: 'HospitalizationSummaryColObservation' }) },
    ],
  }), [intl, typeList])

  if (state === 'denied') {
    return <HospitalizationGridNotice state={state} errorMessageId="HospitalizationSummaryEvolutionsError" onRetry={retry} />
  }

  return (
    <div className="nv-hosp-grid">
      <HospitalizationGridNotice state={state} errorMessageId="HospitalizationSummaryEvolutionsError" onRetry={retry} />
      <div className="nv-hosp-table">
        <RemoteTable
          ref={tableRef}
          idTable={EVOLUTIONS_TABLE_ID}
          dataKey="evolutionId"
          getItems={loadItems}
          params={params}
          paramsDelay={0}
          filterBar={filterBar}
          filtersType={FILTERS_TYPE}
          defaultMultiSortMeta={DEFAULT_SORT}
          refreshButton
          resizableColumns
          tableMinWidth={TABLE_MIN_WIDTH}
          // Con error el cuerpo no dice «Sin evoluciones»: lo que pasó lo dice el aviso de arriba
          notFoundText={state === 'ok' ? intl.formatMessage({ id: 'HospitalizationSummaryEvolutionsEmpty' }) : ' '}
          minHeight={480}
        >
          <RColumn
            field="patientName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColPatient' })}
            grow={COLUMN_WIDTH.patient}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: HospitalizationEvolutionRowDTO) => textOrDash(row.patientName)}
          />
          <RColumn
            field="professionalName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColProfessional' })}
            grow={COLUMN_WIDTH.professional}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: HospitalizationEvolutionRowDTO) => textOrDash(row.professionalName)}
          />
          <RColumn
            field="date"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColDate' })}
            grow={COLUMN_WIDTH.date}
            sortable
            filter
            filterElement={DateFilterTemplate}
            body={(row: HospitalizationEvolutionRowDTO) => formatIsoMoment(row.date)}
          />
          <RColumn
            field="scoreId"
            sortField="scoreDescription"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColEvolutionType' })}
            grow={COLUMN_WIDTH.type}
            sortable
            filter
            filterElement={typeFilter}
            body={(row: HospitalizationEvolutionRowDTO) => textOrDash(row.scoreDescription)}
          />
          <RColumn
            field="sectorName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColSector' })}
            grow={COLUMN_WIDTH.sector}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: HospitalizationEvolutionRowDTO) => textOrDash(row.sectorName)}
          />
          <RColumn
            field="observation"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColObservation' })}
            grow={COLUMN_WIDTH.observation}
            filter
            filterElement={stringFilterTemplate}
            // Texto plano: React lo escapa y el tooltip lo muestra como texto; nunca se interpreta como HTML
            body={(row: HospitalizationEvolutionRowDTO) => (row.observation && row.observation.trim() !== ''
              ? <span className="nv-truncate nv-tip" data-pr-tooltip={row.observation}>{row.observation}</span>
              : EMPTY_VALUE)}
          />
        </RemoteTable>
      </div>
    </div>
  )
}
